import type { UIAudit } from "@/packages/types";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  FileCheck,
  FileX,
  Info,
  ListTodo,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui";
import type { WidgetVariant } from "./WidgetCard";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface AISummaryCardProps {
  audit: UIAudit;
  isGenerating: boolean;
  onOpenSheet: () => void;
  onGenerate: () => void;
  isTerminal?: boolean;
  index?: number;
  variant?: WidgetVariant;
}

// Custom AI icon — layered circles suggesting neural processing
function AIAnalysisIcon({ animate }: { animate?: boolean }) {
  return (
    <motion.svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="text-accent"
      aria-hidden="true"
      animate={animate ? { rotate: 360 } : undefined}
      transition={
        animate
          ? {
              repeat: Number.POSITIVE_INFINITY,
              duration: 4,
              ease: "linear",
            }
          : undefined
      }
    >
      <circle
        cx="8"
        cy="8"
        r="7"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.3"
      />
      <circle
        cx="8"
        cy="8"
        r="4"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.6"
      />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    </motion.svg>
  );
}

const analysisPhrases = [
  "Reading checklist data...",
  "Evaluating document coverage...",
  "Assessing risk factors...",
  "Forming recommendation...",
];

function GeneratingContent() {
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex((i) => (i + 1) % analysisPhrases.length);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col h-full relative">
      {/* Indeterminate progress bar */}
      <div className="h-0.5 w-full bg-border/30 rounded-full overflow-hidden mb-4">
        <div className="h-full w-1/3 bg-accent/60 rounded-full ai-scan-bar" />
      </div>

      {/* Skeleton preview of what summary will look like */}
      <div className="flex-1 flex flex-col gap-3 opacity-40">
        <div className="h-7 w-32 bg-border/50 rounded-full" />
        <div className="h-3 w-full bg-border/40 rounded" />
        <div className="h-3 w-4/5 bg-border/40 rounded" />
        <div className="h-3 w-3/5 bg-border/40 rounded" />
        <div className="h-3 w-full bg-border/40 rounded mt-2" />
        <div className="h-3 w-2/3 bg-border/40 rounded" />
      </div>

      {/* Scan line overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="ai-scan-line" />
      </div>

      {/* Phase text at bottom */}
      <div className="mt-auto pt-3 border-t border-border/30">
        <AnimatePresence mode="wait">
          <motion.span
            key={phraseIndex}
            className="text-xs text-accent font-medium"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3 }}
          >
            {analysisPhrases[phraseIndex]}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

export function AISummaryCard({
  audit,
  isGenerating,
  onOpenSheet,
  onGenerate,
  isTerminal,
  index,
  variant,
}: AISummaryCardProps) {
  const hasSummary = !!audit.aiSummaryData;

  // Generating state
  if (isGenerating) {
    return (
      <WidgetCard
        size="S"
        title="AI Analysis"
        icon={<AIAnalysisIcon animate />}
        isTerminal={isTerminal}
        index={index}
        variant={variant}
        className="!h-[400px] row-span-2"
      >
        <GeneratingContent />
      </WidgetCard>
    );
  }

  // Has summary state
  if (hasSummary) {
    const recommendation =
      audit.aiSummaryData?.aiAnalysis?.approvalRecommendation?.recommendation;
    const sentiment =
      recommendation === "yes"
        ? "positive"
        : recommendation === "no"
          ? "negative"
          : "neutral";

    const riskCount = audit.aiSummaryData?.risks?.length ?? 0;
    const remainingCount = audit.aiSummaryData?.remainingWork?.length ?? 0;
    const docsSufficient =
      audit.aiSummaryData?.computedMetrics?.documentCoverage?.isSufficient;

    const sentimentConfig = {
      positive: {
        label: "Ready for Approval",
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-l-emerald-500",
        icon: <CheckCircle size={14} />,
      },
      negative: {
        label: "Needs Attention",
        bg: "bg-amber-50",
        text: "text-amber-700",
        border: "border-l-amber-500",
        icon: <AlertTriangle size={14} />,
      },
      neutral: {
        label: "Pending Analysis",
        bg: "bg-gray-50",
        text: "text-gray-500",
        border: "border-l-gray-400",
        icon: <Info size={14} />,
      },
    } as const;

    const s = sentimentConfig[sentiment];

    return (
      <WidgetCard
        size="S"
        title="AI Analysis"
        icon={<AIAnalysisIcon />}
        onClick={onOpenSheet}
        isTerminal={isTerminal}
        index={index}
        variant={variant}
        className={`!h-[400px] row-span-2 border-l-2 ${s.border}`}
      >
        <div className="flex flex-col h-full">
          {/* Sentiment banner */}
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${s.bg} ${s.text} -mx-0.5`}
          >
            {s.icon}
            <span className="text-sm font-semibold tracking-tight">
              {s.label}
            </span>
          </div>

          {/* Compact stat line */}
          <div className="flex items-center gap-1.5 text-xs text-muted mt-3 px-0.5">
            <span
              className={`inline-flex items-center gap-1 ${riskCount > 0 ? "text-amber-600" : "text-emerald-600"}`}
            >
              <AlertTriangle size={11} />
              {riskCount} risks
            </span>
            <span className="text-border">|</span>
            <span className="inline-flex items-center gap-1">
              <ListTodo size={11} />
              {remainingCount} remaining
            </span>
            <span className="text-border">|</span>
            <span
              className={`inline-flex items-center gap-1 ${docsSufficient ? "text-emerald-600" : "text-amber-600"}`}
            >
              {docsSufficient ? <FileCheck size={11} /> : <FileX size={11} />}
              Docs {docsSufficient ? "ok" : "gaps"}
            </span>
          </div>

          {/* Summary text with fade */}
          <div
            className="flex-1 min-h-0 overflow-hidden mt-3"
            style={{
              maskImage:
                "linear-gradient(to bottom, black 60%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, black 60%, transparent 100%)",
            }}
          >
            <p className="text-sm text-secondary leading-relaxed">
              {audit.aiSummaryData?.summary}
            </p>
          </div>

          {/* Bottom action zone */}
          <div className="mt-auto pt-2.5 border-t border-border/40">
            <button
              type="button"
              className="group flex items-center justify-between w-full text-xs font-medium text-accent hover:text-accent/80 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSheet();
              }}
            >
              <span>View full analysis</span>
              <ArrowRight
                size={13}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>
        </div>
      </WidgetCard>
    );
  }

  // Empty state
  return (
    <WidgetCard
      size="S"
      title="AI Analysis"
      icon={
        <span className="relative flex items-center justify-center">
          <AIAnalysisIcon />
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-accent ai-pulse-dot" />
        </span>
      }
      isTerminal={isTerminal}
      index={index}
      variant={variant}
      className="!h-[400px] row-span-2"
    >
      <div className="flex flex-col h-full">
        {/* Ambient mesh background */}
        <div className="flex-1 flex flex-col items-center justify-center text-center relative overflow-hidden rounded-lg">
          <div className="ai-mesh-bg absolute inset-0 opacity-30" />
          <div className="relative z-10 flex flex-col items-center gap-2 px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-12 h-12 rounded-xl bg-accent/8 border border-accent/15 flex items-center justify-center mb-1">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-accent/70"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    opacity="0.3"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    opacity="0.5"
                  />
                  <circle cx="12" cy="12" r="2" fill="currentColor" />
                </svg>
              </div>
            </motion.div>
            <span className="text-sm font-semibold text-primary tracking-tight">
              Analyze this audit
            </span>
            <span className="text-xs text-muted leading-relaxed max-w-[200px]">
              AI will assess readiness, flag risks, and recommend next steps
            </span>
          </div>
        </div>

        {/* Full-width bottom action */}
        {!isTerminal && (
          <div className="mt-auto pt-3 border-t border-border/40">
            <Button
              variant="secondary"
              size="sm"
              className="w-full bg-accent/8 text-accent border border-accent/20 hover:bg-accent/15 font-medium"
              onClick={onGenerate}
            >
              Generate Analysis
            </Button>
          </div>
        )}
      </div>
    </WidgetCard>
  );
}

export function AISummaryCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="S" index={index} />;
}
